import { Flex, FormControl, FormLabel, Heading, Text } from "@chakra-ui/react";
import { FormikProps } from "formik";
import React from "react";
import { MultiSelectInput } from "src/components/Inputs/MultiSelectInput";
import { useGlossary } from "src/hooks/useGlossary";
import * as GQL from "src/types/graphql";
import {
  SCHOOL_CONFLICT_SUMMARY,
  useFieldError,
  useSchoolErrorById,
} from "./context";
import { RuleTip } from "./RuleTip";
import { FormEngineRuleFormData } from "./schemas";
import { School, TagType } from "./shared";

type Props = {
  formik: FormikProps<FormEngineRuleFormData>;
  schools: School[];
  tags: TagType[];
  unavailableSchoolIds: string[];
};

export const AutoPlacement: React.FC<Props> = ({
  formik,
  schools,
  tags,
  unavailableSchoolIds,
}) => {
  const { glossary } = useGlossary();
  const schoolErrorById = useSchoolErrorById();
  const schoolIdsError = useFieldError("school_ids");
  const tagIdsError = useFieldError("tag_ids");
  const sourceStatusesError = useFieldError("source_statuses");

  const availableSchools = schools.filter(
    (school) =>
      !unavailableSchoolIds.includes(school.id) ||
      formik.values.school_ids.includes(school.id)
  );

  const schoolOptions = availableSchools
    .map((school) => ({
      id: school.id,
      label: school.name,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const formStatusOptions = [
    { id: GQL.form_status_enum.Submitted, label: glossary`Submitted` },
    { id: GQL.form_status_enum.Verified, label: glossary`Verified` },
    {
      id: GQL.form_status_enum.LotteryReady,
      label: glossary`Ready for lottery`,
    },
    { id: GQL.form_status_enum.Admissions, label: glossary`Admissions` },
  ];

  const tagOptions = [...tags]
    .sort((a, b) => {
      const aGroup = a.tag_group?.name || "Ungrouped";
      const bGroup = b.tag_group?.name || "Ungrouped";
      if (aGroup === bGroup) {
        return a.name.localeCompare(b.name);
      }
      return aGroup.localeCompare(bGroup);
    })
    .map((tag) => ({
      id: tag.id,
      label: tag.name,
      group: tag.tag_group?.name || "Ungrouped",
    }));

  const schoolError =
    schoolIdsError ??
    (schoolErrorById.size > 0 ? SCHOOL_CONFLICT_SUMMARY : undefined);

  return (
    <Flex
      direction="column"
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
    >
      <Flex direction="column" gap="0.5rem" p="1rem">
        <Heading as="h4" size="sm">
          What would you like to do?
        </Heading>
        <Flex direction="column">
          <Text fontWeight="semibold" fontSize="sm">
            Place students at top ranked school
          </Text>
          <Text fontSize="sm" color="gray.600">
            When the form is in a selected status, if the school in rank
            position 1 is eligible for auto placement, set that school&apos;s
            sub-status to Accepted.
          </Text>
        </Flex>
      </Flex>

      <Flex
        direction="column"
        gap="0.5rem"
        p="1rem"
        borderTop="1px solid"
        borderTopColor="gray.200"
      >
        <Heading as="h4" size="sm">
          When would you like to auto place?
        </Heading>

        <FormControl
          isRequired
          isInvalid={sourceStatusesError !== undefined}
          pb="1rem"
        >
          <FormLabel pb="0.325rem" m="0">
            Select the form status for which the rule will apply.
          </FormLabel>
          <MultiSelectInput
            size="md"
            options={formStatusOptions}
            initialValues={formik.values.source_statuses.map((status) => {
              const option = formStatusOptions.find(
                (item) => item.id === status
              );
              return {
                id: status,
                label: option ? option.label : status,
              };
            })}
            onChange={(selectedStatuses) => {
              formik.setFieldValue(
                "source_statuses",
                selectedStatuses.map((status) => status.id)
              );
            }}
            placeholder="Select multiple"
            renderDropdownItem={(item) => (
              <Flex key={item.id}>{item.label}</Flex>
            )}
            error={sourceStatusesError}
          />
          <Text fontSize="sm" color="gray.600" mt="0.5rem">
            Submitted is required. You can also select additional statuses.
          </Text>
        </FormControl>

        <FormControl pb="1rem" isInvalid={schoolError !== undefined}>
          <FormLabel pb="0.325rem" m="0">
            Which schools should this rule apply to?
          </FormLabel>
          <MultiSelectInput
            size="md"
            options={schoolOptions}
            initialValues={formik.values.school_ids.map((schoolId) => {
              const school = schools.find((item) => item.id === schoolId);
              return {
                id: schoolId,
                label: school ? school.name : schoolId,
              };
            })}
            onChange={(selectedSchools) => {
              formik.setFieldValue(
                "school_ids",
                selectedSchools.map((school) => school.id)
              );
            }}
            placeholder="Select multiple"
            error={schoolError}
            isInvalidItem={(item) => schoolErrorById.has(item.id)}
            getInvalidItemMessage={(item) => schoolErrorById.get(item.id)}
          />
          <Text fontSize="sm" color="gray.600" mt="0.5rem">
            The rule will apply only to the selected schools. If no schools are
            selected, it will apply to all schools.
          </Text>
        </FormControl>

        <FormControl isInvalid={tagIdsError !== undefined}>
          <FormLabel pb="0.325rem" m="0">
            Which tags make a student eligible for auto placement?
          </FormLabel>
          <MultiSelectInput
            size="md"
            options={tagOptions}
            initialValues={formik.values.tag_ids.map((tagId) => {
              const tag = tags.find((item) => item.id === tagId);
              return {
                id: tagId,
                label: tag ? tag.name : tagId,
                group: tag?.tag_group?.name || "Ungrouped",
              };
            })}
            onChange={(selectedTags) => {
              formik.setFieldValue(
                "tag_ids",
                selectedTags.map((tag) => tag.id)
              );
            }}
            placeholder="Select multiple"
            renderDropdownItem={(item) => (
              <Flex key={item.id}>{item.label}</Flex>
            )}
            error={tagIdsError}
          />
          <Text fontSize="sm" color="gray.600" mt="0.5rem">
            A student is eligible if their top ranked school choice has any of
            the selected tags. If no tags are selected, this rule will not
            consider tags.
          </Text>
        </FormControl>
      </Flex>

      {formik.values.rule_type && <RuleTip formik={formik} />}
    </Flex>
  );
};
